import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';

import { CLIPS_PAGE_SIZE, getNextClipsOffset } from '@/features/clips/api/infinite-clips-page';
import { clipsQueryKeys } from '@/features/clips/api/query-keys';
import { TClip } from '@/features/clips/types';
import { mediaManagerClient } from '@/lib/axios';
import { QueryConfig } from '@/lib/react-query';
import { ProgramBroadcastFiltersState } from '@/stores/program-broadcast-filters';
import { PaginatedResponse } from '@/types/api';

export async function getSegmentClipById(
  id: string,
  filters?: Partial<ProgramBroadcastFiltersState>
): Promise<PaginatedResponse<TClip, undefined>> {
  return mediaManagerClient.get(`/api/v1/segments/${id}/clips`, {
    params: {
      limit: filters?.rowsPerPage,
      offset: filters?.offset,
      ...(filters?.workspaces &&
        filters?.workspaces.length > 0 && {
          workspace_id__in: filters.workspaces.join(','),
        }),
    },
  });
}

type UseSegmentIdParams = {
  id: string;
  filters?: Partial<ProgramBroadcastFiltersState>;
};

export function useGetSegmentClips({
  id,
  filters,
  config,
}: UseSegmentIdParams & { config?: QueryConfig<typeof getSegmentClipById> }) {
  return useQuery({
    enabled: !!id,
    ...config,
    queryKey: clipsQueryKeys.bySegment(id, filters),
    queryFn: () => getSegmentClipById(id, filters),
  });
}

export function useInfiniteSegmentClips({ id, filters }: UseSegmentIdParams) {
  return useInfiniteQuery({
    enabled: !!id,
    // Holds the previous segment's cuts on screen until the new segment's first batch lands,
    // rather than tearing the panel down and back up. See `useInfiniteBroadcastClips`.
    //
    // RISK: only while there is a segment to list. With no active segment — the segments view
    // clears it when the franja list filters down to nothing — keeping the previous result would
    // leave the panel showing the cuts of a segment nobody has selected.
    placeholderData: id ? keepPreviousData : undefined,
    queryKey: clipsQueryKeys.infiniteBySegment(id, filters),
    queryFn: ({ pageParam }) =>
      getSegmentClipById(id, { ...filters, offset: pageParam, rowsPerPage: CLIPS_PAGE_SIZE }),
    initialPageParam: 0,
    getNextPageParam: getNextClipsOffset,
  });
}
